import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, DollarSign, ExternalLink, Link as LinkIcon, TrendingUp, Share2, Users } from "lucide-react";
import { toast } from "sonner";

const EncurtaPage = () => {
  const [url, setUrl] = useState("");
  const [shortUrl, setShortUrl] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  const handleShorten = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url) {
      toast.error("Por favor, insira uma URL válida");
      return;
    }
    
    // Simulando encurtamento de URL (mockado)
    const mockShortUrl = `https://encurta.net/${Math.random().toString(36).substring(2, 8)}`;
    setShortUrl(mockShortUrl);
    toast.success("URL encurtada com sucesso!");
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    toast.success("Link copiado para a área de transferência!");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent py-2">
            Encurta.net
          </h1>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Encurte seus links e ganhe dinheiro compartilhando-os. Uma plataforma simples e eficiente para monetizar seus links.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Encurte seus Links</CardTitle>
              <CardDescription>Cole sua URL longa e obtenha um link curto</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleShorten} className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Input 
                    type="url"
                    placeholder="Cole sua URL aqui"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    className="flex-grow"
                  />
                  <Button 
                    type="submit" 
                    className="bg-primary hover:bg-primary/90 text-primary-foreground whitespace-nowrap"
                  >
                    <LinkIcon className="mr-2 h-4 w-4" /> Encurtar URL
                  </Button>
                </div>
                
                {shortUrl && (
                  <div className="bg-muted p-4 rounded-lg">
                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                      <div className="flex-grow truncate">
                        <p className="text-sm text-muted-foreground mb-1">URL encurtada:</p>
                        <p className="font-medium">{shortUrl}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => handleCopy(shortUrl)}
                        className={copied === shortUrl ? "bg-primary/10" : ""}
                      >
                        <Copy size={16} className="mr-1" /> 
                        {copied === shortUrl ? "Copiado!" : "Copiar"}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Crie sua Conta</CardTitle>
              <CardDescription>Comece a ganhar dinheiro hoje</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  Registre-se no Encurta.net e comece a monetizar seus links. Ganhe dinheiro a cada clique em seus links encurtados.
                </p>
                <Button 
                  asChild
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a 
                    href="https://encurta.net/ref/killer56" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Criar Conta Grátis
                  </a>
                </Button>
                <div className="bg-muted p-3 rounded-lg text-center text-sm text-muted-foreground">
                  <p>Use o código de referência: <span className="font-bold">killer56</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="como-funciona" className="mb-12">
          <TabsList className="grid grid-cols-2 md:w-[600px] mx-auto">
            <TabsTrigger value="como-funciona">Como Funciona</TabsTrigger>
            <TabsTrigger value="ganhe-dinheiro">Ganhe Dinheiro</TabsTrigger>
          </TabsList>
          
          <TabsContent value="como-funciona" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Como Funciona o Encurta.net</CardTitle>
                <CardDescription>Entenda o processo em 3 passos simples</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <LinkIcon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">1. Encurte</h3>
                    <p className="text-sm text-muted-foreground">Cole o link que deseja encurtar e obtenha uma URL mais curta e amigável.</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <Share2 className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">2. Compartilhe</h3>
                    <p className="text-sm text-muted-foreground">Compartilhe o link encurtado em suas redes sociais, sites, blogs ou WhatsApp.</p>
                  </div>
                  
                  <div className="flex flex-col items-center text-center p-4 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-3 rounded-full mb-4">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">3. Ganhe</h3>
                    <p className="text-sm text-muted-foreground">Receba pagamentos por cada clique que seus links recebem. Quanto mais cliques, mais você ganha.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="ganhe-dinheiro" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Ganhe Dinheiro com Encurta.net</CardTitle>
                <CardDescription>Monetize seus links e aumente sua renda</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-muted p-4 rounded-lg">
                      <h3 className="text-lg font-medium mb-2 flex items-center">
                        <TrendingUp className="h-5 w-5 mr-2 text-primary" /> Ganhos por Clique
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Receba pagamentos por cada clique em seus links encurtados. Os valores variam de acordo com a localização geográfica do visitante.
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                        <li>Visitantes do Brasil: R$X por 1000 visualizações</li>
                        <li>Visitantes dos EUA/Europa: R$X por 1000 visualizações</li>
                        <li>Visitantes de outros países: R$X por 1000 visualizações</li>
                      </ul>
                    </div>
                    
                    <div className="bg-muted p-4 rounded-lg">
                      <h3 className="text-lg font-medium mb-2 flex items-center">
                        <Users className="h-5 w-5 mr-2 text-primary" /> Programa de Referência
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Ganhe ainda mais indicando amigos para o Encurta.net. Receba uma comissão por cada novo usuário registrado através do seu link.
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                        <li>10% de comissão sobre os ganhos dos seus referidos</li>
                        <li>Link personalizado para divulgação</li>
                        <li>Acompanhamento em tempo real dos seus referidos</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                    <h3 className="text-lg font-medium mb-2 text-center">Formas de Pagamento</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div className="p-3">
                        <p className="font-medium">Pix</p>
                        <p className="text-xs text-muted-foreground">Mínimo: R$20</p>
                      </div>
                      <div className="p-3">
                        <p className="font-medium">PayPal</p>
                        <p className="text-xs text-muted-foreground">Mínimo: R$50</p>
                      </div>
                      <div className="p-3">
                        <p className="font-medium">Transferência Bancária</p>
                        <p className="text-xs text-muted-foreground">Mínimo: R$100</p>
                      </div>
                      <div className="p-3">
                        <p className="font-medium">Bitcoin</p>
                        <p className="text-xs text-muted-foreground">Mínimo: R$50</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="text-center">
          <Button 
            asChild 
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <a 
              href="https://encurta.net/ref/killer56" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-5 w-5" /> Criar Conta no Encurta.net
            </a>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            Ao se registrar usando nosso link de referência, você nos ajuda a manter este site no ar.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default EncurtaPage;

